import React, { useMemo, useRef, useState } from 'react';
import { vars } from '@croquiscom-pvt/zds/tokens';
import styled from '@emotion/styled';
import debounce from 'lodash/debounce';

import { Toast } from '@common/overlay-manager';
import { Typography } from '@common/styles';

interface Props {
  name: string;
  is_required: boolean;
  max_length: number;
  value: string;
  onChange?: (value: string) => void;
  onFocus?: (value: string) => void;
  onBlur?: (value: string) => void;
  onKeyPress?: (keyCode: number, target: EventTarget & HTMLInputElement) => void;
}

export const TextField = ({
  name,
  is_required,
  max_length,
  value,
  onChange = () => undefined,
  onFocus = () => undefined,
  onBlur = () => undefined,
  onKeyPress = () => undefined,
}: Props) => {
  const [focused, setFocused] = useState(false);
  const shadowEl = useRef<HTMLLabelElement>(null);
  const visible_label = !!name;

  const placeholder = `${is_required ? '[필수]' : '[선택]'} 입력해 주세요`;

  const debouncedToastShow = debounce((msg: string) => Toast.show(msg), 200);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.currentTarget.value.length > max_length) {
      debouncedToastShow(`텍스트는 ${max_length}자 이하로 입력해 주세요.`);
    } else {
      onChange(e.currentTarget.value);
    }
  };

  const handleFocus = (e: React.SyntheticEvent<HTMLInputElement>) => {
    setFocused(true);
    onFocus(e.currentTarget.value);
  };

  const handleBlur = (e: React.SyntheticEvent<HTMLInputElement>) => {
    setFocused(false);
    onBlur(e.currentTarget.value);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    onKeyPress(e.which, e.currentTarget);
  };

  const labelHeight = useMemo(() => {
    if (focused && shadowEl.current) {
      return shadowEl.current.clientHeight;
    }
    return 16;
  }, [focused, shadowEl.current]);

  return (
    <Wrapper>
      {visible_label && (
        <Label style={{ height: labelHeight }} focused={focused} className={Typography.BODY_13_SEMIBOLD}>
          <Shadow ref={shadowEl}>{name}</Shadow>
          {name.replace(/&nbsp;|&#160;/g, ' ')}
        </Label>
      )}
      <TextFieldBox focused={focused}>
        <Input
          type={'text'}
          className={Typography.BODY_16_SEMIBOLD}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyPress={handleKeyPress}
        />
        <ValueLength className={Typography.BODY_13_SEMIBOLD}>
          {value.length}/{max_length}
        </ValueLength>
      </TextFieldBox>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  margin: 0 0 8px;
`;

const Label = styled.label<{ focused: boolean }>`
  display: block;
  overflow: hidden;
  margin: 0 0 6px;
  padding: 0 4px;
  color: ${vars.v3Color.gray500};
  font-size: 13px;
  font-weight: 500;
  line-height: 16px;
  transition: height 0.2s ease-in-out;
  ${({ focused }) =>
    !focused &&
    `
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      display: -webkit-box;
      overflow: hidden;
      text-overflow: ellipsis;
    `}
`;

const Shadow = styled.span`
  display: block;
  visibility: hidden;
  position: absolute;
  z-index: -1;
`;

const TextFieldBox = styled.div<{ focused: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 48px;
  overflow: hidden;
  border: 2px solid ${vars.v3Color.gray100};
  border-radius: 8px;
  background: #fff;
  transition: height 0.2s ease-in-out;
  ${({ focused }) => focused && `border: 2px solid ${vars.v3Color.gray200}`}
`;

const Input = styled.input`
  max-width: 320px;
  width: 100%;
  height: 100%;
  padding: 13px 0 13px 16px;
  line-height: inherit;

  &::placeholder,
  &::-webkit-input-placeholder,
  &::-moz-placeholder,
  &:-moz-placeholder,
  &:-ms-input-placeholder {
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
    vertical-align: middle;
    color: ${vars.v3Color.gray300};
  }
`;

const ValueLength = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 54px;
  height: 100%;
  padding: 13px;
  color: ${vars.v3Color.gray300};
`;
